import { Component, inject, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonComponent, ListTableComponent, ListTableDataLoader, PopupDialogComponent } from '@ppl/ui';
import { UserService } from '../../../../services/user.service';
import { HttpRequestService } from '../../../../shared/services/http-request.service';
import { ENDPOINTS } from '../../../../shared/constants/url.constants';
import { formatUrl } from '../../../../shared/utils/format-url-util';
import { NotificationService } from '../../../../shared/services/notification.service';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';

@Component({
  selector: 'ppl-mass-adjustments',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, ListTableComponent, PopupDialogComponent],
  templateUrl: './mass-adjustments.component.html',
  styles: [
    `
      .table-style {
        width: 100%;
      }

      .custom-row-actions {
        display: flex;
        width: 100%;
      }

      .custom-row-actions__container {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: nowrap;
      }
    `
  ]
})
export class MassAdjustmentsComponent {
  @ViewChild('massAdjustmentTable') massAdjustmentTable!: ListTableComponent;
  @ViewChild(ListTableComponent) listTable!: ListTableComponent;
  private allowListStore = inject(AllowListAuthStore);

  pageId = PAGE_CONSTANTS.STAFF_MASS_ADJUSTMENTS;
  isReadOnly = false;
  programName = '';
  lazyLoad = true;
  sharedDataKey = 'list_mass_adjustments';
  showDeleteDialog = false;
  deleteMessage = '';
  batchToDelete = '';
  dialogType: 'approval' | 'rejection' | 'warning' = 'approval';
  warningMessage = '';
  showGenerateDialog = false;
  checkedInProgressApproval = false;
  comments = '';
  selectedRows: any[] = [];
  private destroy$ = new Subject<void>();
  massAdjustmentsDataLoader: ListTableDataLoader = (payload, context) => {
    return this.http.post(ENDPOINTS.LIST_PAGE_DATA, payload, context?.hasUpdatedEntry);
  };

  constructor(
    private userService: UserService,
    private router: Router,
    private http: HttpRequestService,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.setPageAccess();
  }

  private setPageAccess() {
    const authConfig = this.allowListStore.getConfig();
    if (authConfig && !authConfig.pages[this.pageId]) {
      this.isReadOnly = true;
    }
  }

  onAddNewAdjustment(): void {
    this.router.navigate(['/home/addBulkAdjustment']);
  }

  onRowClicked(event: any): void {
    if (!event?.batchInstanceId) {
      console.log('No batchInstanceId found in event:', event);
      return;
    }

    const batchInstanceId = event.batchInstanceId;
    const url = formatUrl(ENDPOINTS.MASS_ADJUSTMENT.GET_DETAILS, {
      programCode: this.programName,
      batchInstanceId
    });

    this.http.get(url).subscribe({
      next: (response) => {
        this.router.navigate(['/home/addBulkAdjustment'], {
          state: { editData: response, batchInstanceId }
        });
      },
      error: (error) => {
        console.error('Failed to fetch bulk adjustment details:', error);
        this.notificationService.error('Error', 'Failed to load bulk adjustment details');
      }
    });
  }

  onViewBatch(batchInstanceId: string, batchProcessCode: string, batchType: string, batchStatus: string): void {
    this.router.navigate(['/home/bulkBatchResultList'], { state: { batchInstanceId, batchProcessCode, batchType, batchStatus } });
  }

  onProcessBatch(batchInstanceId: string): void {
    this.router.navigate(['/home/bulkBatchErrorList'], { state: { batchInstanceId } });
  }

  onDeleteBatch(batchInstanceId: string): void {
    this.batchToDelete = batchInstanceId;
    this.deleteMessage = 'Are you sure you want to delete this bulk adjustment batch? This action cannot be undone.';
    this.showDeleteDialog = true;
  }

  confirmDelete(): void {
    const batchInstanceId = this.batchToDelete;
    const url = formatUrl(ENDPOINTS.MASS_ADJUSTMENT.DELETE_BATCH, {
      programCode: this.programName,
      batchInstanceId
    });

    this.http.delete(url).subscribe({
      next: () => {
        this.notificationService.success('Success', 'Bulk adjustment batch deleted successfully');
        this.sharedDataKey = `list_mass_adjustments_${Date.now()}`;
        this.showDeleteDialog = false;
        this.batchToDelete = '';
      },
      error: (error) => {
        console.error('Failed to delete bulk adjustment:', error);
        this.notificationService.error('Error', 'Failed to delete bulk adjustment batch');
        this.showDeleteDialog = false;
      }
    });
  }

  cancelDelete(): void {
    this.showDeleteDialog = false;
    this.batchToDelete = '';
  }

  onListAction(event: any) {
    const btn = event?.button;
    const selected = event?.selectedCheckBox ?? [];

    if ((btn?.buttonAction === 'approveMassAdjustment' || btn?.buttonAction === 'rejectMassAdjustment') && selected.some((row: any) => row?.batchStatus !== "Awaiting Review")) {
      this.dialogType = 'warning';
      this.warningMessage = 'Only Awaiting Review records can be approved or rejected. Please adjust your selection and try again.';
      this.showGenerateDialog = true;
      this.checkedInProgressApproval = true;
      return;
    }

    // Store selected rows for API call
    this.selectedRows = selected;
    this.comments = '';

    if (btn?.buttonAction === 'approveMassAdjustment') {
      this.dialogType = 'approval';
      this.showGenerateDialog = true;
    } else if (btn?.buttonAction === 'rejectMassAdjustment') {
      this.dialogType = 'rejection';
      this.showGenerateDialog = true;
    }
  }

  onConfirm() {
    if (this.dialogType === 'warning') {
      // Just close the dialog for warning
      this.showGenerateDialog = false;
      this.checkedInProgressApproval = false;
      return;
    }

    // Validate rejection requires comments
    if (this.dialogType === 'rejection' && (!this.comments || this.comments.trim().length === 0)) {
      // Don't proceed if no comments are provided for rejection
      return;
    }

    if (this.dialogType === 'approval') {
      this.approveMassAdjustment();
    } else if (this.dialogType === 'rejection') {
      this.rejectMassAdjustment();
    }
  }

  approveMassAdjustment() {
    const payload = {
      batchInstanceIds: this.selectedRows.map(row => row.batchInstanceId),
      batchStatusLkp: 'A',
      remarks: this.comments
    };
    const url = formatUrl(ENDPOINTS.MASS_ADJUSTMENT.APPROVAL, {
      programCode: this.programName,
    });
    this.http.post(url, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Approval successful', response);
          this.showGenerateDialog = false;
          this.notificationService.success('Success', 'Bulk adjustment batch approved successfully');
          this.listTable.reloadDataFromSharedService(this.sharedDataKey);
        },
        error: (error) => {
          console.error('Approval failed', error);
          this.showGenerateDialog = false;
        }
      });
  }

  rejectMassAdjustment() {
    const payload = {
      batchInstanceIds: this.selectedRows.map(row => row.batchInstanceId),
      batchStatusLkp: 'R',
      remarks: this.comments
    };
    const url = formatUrl(ENDPOINTS.MASS_ADJUSTMENT.APPROVAL, {
      programCode: this.programName,
    });
    this.http.post(url, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Rejection successful', response);
          this.showGenerateDialog = false;
          this.notificationService.success('Success', 'Bulk adjustment batch rejected successfully');
          this.listTable.reloadDataFromSharedService(this.sharedDataKey);
        },
        error: (error) => {
          console.error('Rejection failed', error);
          this.showGenerateDialog = false;
        }
      });
  }
}
